import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual padding. `none` when children manage their own (tables, lists). */
  padding?: 'none' | 'sm' | 'md';
  /** Hover treatment for cards that are links or buttons. */
  interactive?: boolean;
  /** Legacy names accepted from older call sites; all render the same surface. */
  variant?: 'default' | 'elevated' | 'outlined';
}

const PADDING = { none: '', sm: 'p-4', md: 'p-5' } as const;

/**
 * Card (DESIGN.md §4): hairline, `rounded-card`, no resting shadow in dark,
 * a 1px tinted shadow in light. Nest at most one level deep.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, padding = 'none', interactive, variant: _variant, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-card border border-line bg-surface',
        '[[data-theme=light]_&]:shadow-[0_1px_2px_rgb(29_29_31/0.06)]',
        interactive &&
          'transition-[background-color,border-color] duration-150 hover:border-line-strong hover:bg-surface-2',
        PADDING[padding],
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1 p-5', className)} {...props} />
  )
);

CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-base font-semibold leading-tight text-ink', className)} {...props} />
  )
);

CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-[13px] text-ink-2', className)} {...props} />
  )
);

CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('p-5 pt-0', className)} {...props} />
);

CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-2 p-5 pt-0', className)} {...props} />
  )
);

CardFooter.displayName = 'CardFooter';

/**
 * Section: a titled group WITHOUT a box. Use this instead of nesting cards.
 */
export function Section({
  title,
  description,
  actions,
  className,
  children,
  as: Heading = 'h2',
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
  as?: 'h2' | 'h3';
}) {
  return (
    <section className={cn('min-w-0 space-y-3', className)}>
      {title || actions ? (
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {title ? <Heading className="text-sm font-semibold text-ink">{title}</Heading> : null}
            {description ? <p className="mt-0.5 text-[13px] text-ink-2">{description}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}
